/**
 * Composable for loading and managing PDF documents with pdf.js
 */

import { ref, shallowRef, computed, onUnmounted, type Ref, type ShallowRef, type ComputedRef } from 'vue'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import type { TextContent } from 'pdfjs-dist/types/src/display/api'
import type { PDFOutlineItem } from '../types'
import { pdfjsDocumentFiles, pdfjsLib, setupPDFJS } from '../utils/pdfjs-setup'

export interface UsePDFDocumentOptions {
  onLoad?: (totalPages: number, outline?: PDFOutlineItem[]) => void
  onError?: (error: Error) => void
}

export interface UsePDFDocumentReturn {
  // State
  document: ShallowRef<PDFDocumentProxy | null>
  isLoading: Ref<boolean>
  isLoaded: ComputedRef<boolean>
  error: Ref<Error | null>
  totalPages: Ref<number>
  outline: Ref<PDFOutlineItem[]>

  // Methods
  loadDocument: (url: string) => Promise<void>
  getPage: (pageNumber: number) => Promise<PDFPageProxy>
  getTextContent: (pageNumber: number) => Promise<TextContent>
  preloadTextContent: () => Promise<void>
  destroy: () => void
}

/**
 * Composable for loading and managing PDF documents
 */
export function usePDFDocument(options: UsePDFDocumentOptions = {}): UsePDFDocumentReturn {
  const { onLoad, onError } = options

  // State
  let currentTask: ReturnType<typeof pdfjsLib.getDocument> | null = null
  /** Which load is the current one: a load `destroy()` left behind has nothing left to say. */
  let latestLoad = 0
  const document = shallowRef<PDFDocumentProxy | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const totalPages = ref(0)
  const outline = ref<PDFOutlineItem[]>([])

  // Page cache for performance
  const pageCache = new Map<number, PDFPageProxy>()

  // Text content cache for search performance
  const textContentCache = new Map<number, TextContent>()

  // Computed
  const isLoaded = computed(() => document.value !== null && !isLoading.value)

  /**
   * Converts pdf.js outline to our PDFOutlineItem format
   */
  async function convertOutline(
    pdfDoc: PDFDocumentProxy,
    pdfOutline: unknown[] | null,
  ): Promise<PDFOutlineItem[]> {
    if (!pdfOutline || pdfOutline.length === 0) {
      return []
    }

    const items: PDFOutlineItem[] = []

    for (const item of pdfOutline as { title: string; dest: string | unknown[] | null; items?: unknown[] }[]) {
      let pageNumber = 1

      // Resolve destination to page number
      if (item.dest) {
        try {
          let dest: unknown[] | null = null
          if (typeof item.dest === 'string') {
            dest = await pdfDoc.getDestination(item.dest)
          } else if (Array.isArray(item.dest)) {
            dest = item.dest
          }
          if (Array.isArray(dest) && dest[0]) {
            const ref = dest[0] as { num: number; gen: number }
            const pageIndex = await pdfDoc.getPageIndex(ref)
            pageNumber = pageIndex + 1
          }
        } catch {
          // Keep default page 1 if destination resolution fails
        }
      }

      const outlineItem: PDFOutlineItem = {
        title: item.title,
        pageNumber,
      }

      // Recursively process children
      if (item.items && item.items.length > 0) {
        outlineItem.children = await convertOutline(pdfDoc, item.items)
      }

      items.push(outlineItem)
    }

    return items
  }

  /**
   * Loads a PDF document from URL
   */
  async function loadDocument(url: string): Promise<void> {
    setupPDFJS()

    // Clean up previous document
    destroy()
    const load = latestLoad

    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`)
      }
      const pdfData = await response.arrayBuffer()
      if (load !== latestLoad) return

      const loadingTask = pdfjsLib.getDocument({
        data: pdfData,
        ...pdfjsDocumentFiles(),
        disableAutoFetch: false,
        disableStream: false,
      })

      currentTask = loadingTask
      const pdfDoc = await loadingTask.promise
      if (load !== latestLoad) return
      document.value = pdfDoc
      totalPages.value = pdfDoc.numPages

      // Load outline (table of contents)
      try {
        const pdfOutline = await pdfDoc.getOutline()
        outline.value = await convertOutline(pdfDoc, pdfOutline)
      } catch {
        outline.value = []
      }

      onLoad?.(pdfDoc.numPages, outline.value.length > 0 ? outline.value : undefined)
    } catch (err) {
      if (load !== latestLoad) return
      const loadError = err instanceof Error ? err : new Error('Failed to load PDF')
      error.value = loadError
      onError?.(loadError)
    } finally {
      if (load === latestLoad) isLoading.value = false
    }
  }

  /**
   * Gets a page from the document (with caching)
   */
  async function getPage(pageNumber: number): Promise<PDFPageProxy> {
    if (!document.value) {
      throw new Error('No document loaded')
    }

    if (pageNumber < 1 || pageNumber > totalPages.value) {
      throw new Error(`Invalid page number: ${pageNumber}`)
    }

    const cached = pageCache.get(pageNumber)
    if (cached) {
      return cached
    }

    const page = await document.value.getPage(pageNumber)
    pageCache.set(pageNumber, page)

    return page
  }

  /**
   * Gets text content for a page (with caching)
   */
  async function getTextContent(pageNumber: number): Promise<TextContent> {
    const cached = textContentCache.get(pageNumber)
    if (cached) return cached

    const page = await getPage(pageNumber)
    const textContent = await page.getTextContent()
    textContentCache.set(pageNumber, textContent)
    return textContent
  }

  /**
   * Preloads text content for all pages in background batches
   */
  async function preloadTextContent(): Promise<void> {
    if (!document.value) return

    const total = totalPages.value
    const BATCH_SIZE = 10
    const BATCH_DELAY = 50

    for (let i = 1; i <= total; i += BATCH_SIZE) {
      const batch: Promise<TextContent>[] = []
      for (let j = i; j < i + BATCH_SIZE && j <= total; j++) {
        if (!textContentCache.has(j)) {
          batch.push(getTextContent(j))
        }
      }
      if (batch.length > 0) {
        await Promise.all(batch)
        if (i + BATCH_SIZE <= total) {
          await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY))
        }
      }
    }
  }

  /**
   * Cleans up the document and cache
   */
  function destroy(): void {
    pageCache.clear()
    textContentCache.clear()

    latestLoad += 1
    // The task owns the document and its worker, loaded or still loading.
    void currentTask?.destroy()
    currentTask = null
    document.value = null

    totalPages.value = 0
    outline.value = []
    error.value = null
  }

  // Cleanup on unmount
  onUnmounted(() => {
    destroy()
  })

  return {
    document,
    isLoading,
    isLoaded,
    error,
    totalPages,
    outline,
    loadDocument,
    getPage,
    getTextContent,
    preloadTextContent,
    destroy,
  }
}
